/**
 * Shared ECharts option-fragment builders.
 *
 * Import these into any chart component to eliminate boilerplate:
 *   import { darkTooltip, bottomLegend, standardGrid, categoryXAxis, valueYAxis, formatMonth, collectLastMonths } from 'utilities/chartUtils';
 *
 * All builders return plain objects — spread them into your ECharts option:
 *   tooltip: { ...darkTooltip(), formatter: (p) => ... }
 */

import { measurementDetailUrl } from 'routePaths';
import { t } from 'utilities/TranslationUtils';

export const TOOLTIP_BG    = '#1F2937';
export const AXIS_COLOR    = '#6B7280';
export const GRID_LINE_COLOR = '#E5E7EB';

/** Default number of months to show in monthly charts. */
export const LAST_N_MONTHS = 5;


export type ResultBucket = 'goal' | 'onTarget' | 'offTarget';

/**
 * The canonical 3-bucket kick palette — the ONE colour set used for every kick marker (pitch, goal,
 * 3D, card preview) AND the result chips, so a given outcome is the same colour everywhere.
 * Positive outcomes are GREEN: goal = deep green, onTarget = green. Negative (offTarget) = red.
 *
 * MUST stay in sync with the chip slug colours in Sessions.scss
 * (`.kick-result-chip--goal / --ontarget / --offtarget`).
 */
export const KICK_RESULT_BUCKET_COLORS = {
  goal:      '#16A34A',
  onTarget:  '#22C55E',
  offTarget: '#DC2626',
} as const;

/**
 * Single registry of every kick-result STATUS that can reach the UI → its polarity bucket. This is
 * the one place a status is declared; `kickResultBucket()` derives from it, and colours come from the
 * status's bucket via KICK_RESULT_BUCKET_COLORS — there is no separate per-result palette to drift.
 * The label for a status is `sessions.kickResult_<status>` by convention.
 *
 * Status universe (verified against SessionsService `SessionResultMapper` + the radar-result enums):
 *   - FreeKick  (FreeKickResult):     Goal, WallHit, Saved, Out, Grounded
 *   - Penalty   (PenaltyResult/override): Goal, OnTarget, Saved, Out, Grounded  (Unsavable/Savable = challenge variants)
 *   - Shooting  (OpenShootingKickStatus): Goal, AwayFromGoal, Saved, Grounded
 *   - Binary kinds (Corner/ThrowIn/Goalkeeper): synthesised to OnTarget / OffTarget only.
 * Any unknown/non-standard string (e.g. "InPlay") falls back to the offTarget bucket;
 * for binary kinds the numeric onTarget flag corrects it (see measurementResultBucket).
 */
export const KICK_RESULT_REGISTRY: Record<string, ResultBucket> = {
  Goal:         'goal',
  Unsavable:    'goal',
  Savable:      'onTarget',
  Saved:        'onTarget',
  OnTarget:     'onTarget',
  WallHit:      'offTarget',
  AwayFromGoal: 'offTarget',
  Out:          'offTarget',
  OffTarget:    'offTarget',
  Grounded:     'offTarget',
};

/**
 * ECharts z-layer order for kick-result scatter series.
 * Goal is always drawn on top, off-target at the bottom.
 * Use these values anywhere scatter series are built by kick result so that
 * the stacking is consistent across all charts.
 */
export const KICK_RESULT_Z = {
  goal:      4,
  onTarget:  3,
  offTarget: 2,
} as const;

/** Shared scatter symbol size for shot/goal charts. */
export const SCATTER_SYMBOL_SIZE = 14;

/** True for a status that is declared in the registry (and so has a `sessions.kickResult_<status>` label). */
export function isKnownKickResult(kickResult: string): boolean {
  return Object.prototype.hasOwnProperty.call(KICK_RESULT_REGISTRY, kickResult);
}

/** Categorise a kickResult string into one of the three standard buckets (derived from the registry). */
export function kickResultBucket(kickResult: string): ResultBucket {
  return KICK_RESULT_REGISTRY[kickResult] ?? 'offTarget';
}

/**
 * On-target threshold for binary kinds: an `onTarget` value (which can be fractional, 0–1) counts as
 * on-target when it is at or above this.
 *
 * MUST stay in sync with the BFF constant `SessionResultMapper.OnTargetThreshold` (C#), which
 * synthesises the binary-kind result string with the same cut. Keeping them equal is what stops a
 * kick's displayed status ("On Target"/"Off Target") from disagreeing with how it's bucketed here.
 */
export const ON_TARGET_THRESHOLD = 0.75;

/**
 * Measurement-aware bucket resolution.
 *
 * For binary kinds (Goalkeeper/ThrowIn/Corner), prefer the numeric `onTarget` flag when present,
 * because result strings can be non-standard in some feeds. Explicit goal-like results always stay
 * in the goal bucket. The flag is classified with the same threshold the BFF used to synthesise the
 * result string (ON_TARGET_THRESHOLD), so bucket and displayed status can't disagree.
 */
export function measurementResultBucket(
  measurement: { result: string; kind?: string | null; onTarget?: number | null },
  kindOverride?: string | null,
): ResultBucket {
  const base = kickResultBucket(measurement.result);
  if (base === 'goal') return 'goal';

  const kind = kindOverride ?? measurement.kind ?? null;
  if (kind != null && isBinaryOnTargetKind(kind) && measurement.onTarget != null) {
    return measurement.onTarget >= ON_TARGET_THRESHOLD ? 'onTarget' : 'offTarget';
  }

  return base;
}

/** Session kinds that use binary OnTarget/OffTarget styling in top/goal pitch views. */
export function isBinaryOnTargetKind(kind: string): boolean {
  return kind === 'Corner' || kind === 'ThrowIn' || kind === 'Goalkeeper';
}

/** Positive outcome (green/amber chip) vs negative (red chip). The single polarity rule. */
export function isPositiveBucket(bucket: ResultBucket): boolean {
  return bucket === 'goal' || bucket === 'onTarget';
}

/**
 * The bucket used for DISPLAY (marker colour AND chip): like measurementResultBucket, but a binary
 * kind's on-target collapses to the `goal` bucket so it reads green (positive). This is the single
 * definition both the chip and every kick marker use, so an outcome is coloured identically anywhere.
 */
export function resultDisplayBucket(
  measurement: { result: string; kind?: string | null; onTarget?: number | null },
  kindOverride?: string | null,
): ResultBucket {
  const bucket = measurementResultBucket(measurement, kindOverride);
  const kind = kindOverride ?? measurement.kind ?? null;
  return bucket === 'onTarget' && kind != null && isBinaryOnTargetKind(kind) ? 'goal' : bucket;
}

/** The canonical display colour for a kick marker — the same colour its chip uses. */
export function kickMarkerColor(
  measurement: { result: string; kind?: string | null; onTarget?: number | null },
  kindOverride?: string | null,
): string {
  return KICK_RESULT_BUCKET_COLORS[resultDisplayBucket(measurement, kindOverride)];
}

/**
 * Single source of truth for the result-chip presentation — colour + generic label per bucket.
 *   goal      → green   ("On Target"/positive)
 *   onTarget  → amber   ("On Target"/positive)
 *   offTarget → red     ("Off Target"/negative)
 * `slug` selects `.kick-result-chip--{slug}`. `genericLabelKey` is the polarity-correct fallback
 * label used whenever the specific result string would contradict the colour (so the chip can never
 * show a negative word on a positive colour, e.g. a green "Off Target").
 */
export const RESULT_BUCKET_CHIP: Record<
  ResultBucket,
  { slug: 'goal' | 'ontarget' | 'offtarget'; genericLabelKey: string }
> = {
  goal:      { slug: 'goal',      genericLabelKey: 'sessions.kickResult_OnTarget' },
  onTarget:  { slug: 'ontarget',  genericLabelKey: 'sessions.kickResult_OnTarget' },
  offTarget: { slug: 'offtarget', genericLabelKey: 'sessions.kickResult_OffTarget' },
};

/**
 * Modern green gradient itemStyle for bucket bar charts.
 * Two-stop gradient: bright emerald at the top, deeper green at the base.
 */
export function glassBucketBarStyle() {
  return {
    color: {
      type: 'linear' as const,
      x: 0, y: 0, x2: 0, y2: 1,
      colorStops: [
        { offset: 0, color: '#4ADE80' },
        { offset: 1, color: '#16A34A' },
      ],
    },
    borderRadius: [6, 6, 0, 0] as [number, number, number, number],
  };
}

/**
 * Calculate a Y-axis range for a percentage chart (0-100%) from the data values, with 10% headroom
 * on top to accommodate bar labels. Handles the edge case where data values might be in decimal
 * format (0-1) by sanitising to percentages, and clamps strictly to 0-100.
 */
export function calculatePercentYAxisRange(dataValues: number[]): { min: number; max: number } {
  // Sanitize input values in case they're decimals (0-1) instead of percentages (0-100)
  const sanitized = dataValues.map(v => v > 1 ? v : v * 100);
  const dataMin = sanitized.length > 0 ? Math.min(...sanitized) : 0;
  const dataMax = sanitized.length > 0 ? Math.max(...sanitized) : 0;

  let min = Math.max(0, dataMin);
  let max = Math.max(dataMax, 5); // ensure some minimum range

  // Add 10% headroom on top for bar labels to have breathing space
  const range = max - min;
  max = max + (range * 0.1);

  // Strictly clamp to 0-100 for percentage charts
  min = Math.max(0, Math.min(100, min));
  max = Math.max(0, Math.min(100, max));

  // Ensure some minimum height
  if (max - min < 5) {
    const mid = (min + max) / 2;
    min = Math.max(0, mid - 2.5);
    max = Math.min(100, mid + 2.5);
  }

  return { min, max };
}


/**
 * Build three scatter series coloured by kick result (Goal / On-target / Off-target).
 *
 * `mapEntry` should return [x, y, ...extra] per entry — extra values are passed through
 * and available in the tooltip formatter's `params.data`.
 */
export function kickResultScatterSeries<T extends { kickResult: string }>(
  entries: T[],
  mapEntry: (e: T) => (number | string | null)[],
  sizes: [number, number, number] = [SCATTER_SYMBOL_SIZE, SCATTER_SYMBOL_SIZE, SCATTER_SYMBOL_SIZE - 2],
): any[] {
  const groups = { goal: [] as (number | string | null)[][], onTarget: [] as (number | string | null)[][], offTarget: [] as (number | string | null)[][] };
  for (const e of entries) {
    groups[kickResultBucket(e.kickResult)].push(mapEntry(e));
  }
  return [
    {
      type: 'scatter', name: t('sessions.kickResult_OffTarget'),
      data: groups.offTarget, symbolSize: sizes[2],
      itemStyle: { color: KICK_RESULT_BUCKET_COLORS.offTarget },
      z: KICK_RESULT_Z.offTarget,
    },
    {
      type: 'scatter', name: t('sessions.kickResult_OnTarget'),
      data: groups.onTarget, symbolSize: sizes[1],
      itemStyle: { color: KICK_RESULT_BUCKET_COLORS.onTarget, borderColor: '#fff', borderWidth: 1 },
      z: KICK_RESULT_Z.onTarget,
    },
    {
      type: 'scatter', name: t('sessions.kickResult_Goal'),
      data: groups.goal, symbolSize: sizes[0],
      itemStyle: { color: KICK_RESULT_BUCKET_COLORS.goal, borderColor: '#fff', borderWidth: 1.5 },
      z: KICK_RESULT_Z.goal,
    },
  ];
}


/** Returns a readable label for a session type string. Falls back to space-separated PascalCase. */
export function formatSessionTypeLabel(type: string): string {
  const key = `sessions.sessionType_${type}`;
  const translated = t(key);
  return translated !== key ? translated : type.replace(/([A-Z])/g, ' $1').trim();
}


/** Format an ISO date-time into "Apr 7, 2026 · 02:30 PM" for tooltips. */
export function formatDateTimeTooltip(dateTime: string): string {
  const dt = new Date(dateTime);
  const dateStr = dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const timeStr = dt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return `${dateStr} · ${timeStr}`;
}

/**
 * Render kick/session ID line for an ECharts HTML tooltip.
 * Each ID is rendered as an underlined span with an onclick handler that copies to clipboard.
 */
export function formatKickIdTooltip(kickId: string, sessionId: string, includeDetailLink = true): string {
  if (!kickId) return '';
  const shortKick = kickId.slice(0, 8);
  const shortSession = sessionId.slice(0, 8);
  const detailUrl = measurementDetailUrl(sessionId, kickId);
  return [
    `<span style="cursor:pointer;text-decoration:underline dotted;opacity:0.8" onclick="navigator.clipboard.writeText('${kickId}')" title="${kickId}">${t('sessions.kickDetailKickId')}: ${shortKick}… <span style='font-size:10px;opacity:0.6'>(${t('sessions.clickToCopy')})</span></span>`,
    `<span style="cursor:pointer;text-decoration:underline dotted;opacity:0.8" onclick="navigator.clipboard.writeText('${sessionId}')" title="${sessionId}">${t('sessions.kickDetailSessionId')}: ${shortSession}… <span style='font-size:10px;opacity:0.6'>(${t('sessions.clickToCopy')})</span></span>`,
    // Omit the "open details" link when the tooltip is shown on that measurement's own detail page.
    includeDetailLink ? `<a href="${detailUrl}" style="color:#4c8ef7;text-decoration:underline">${t('sessions.kickDetailOpenDetails')} →</a>` : '',
  ].filter(Boolean).join('<br/>');
}


/**
 * Dark card tooltip base — override `formatter` and optionally `position` per chart.
 *
 * `enterable: true` keeps the tooltip alive when the cursor moves inside it, allowing
 * users to click interactive content (copy-ID spans, labels, etc.).
 * `hideDelay: 300` gives 300 ms grace time to travel from the data point to the tooltip
 * before it closes. Both must be set together — enterable alone is not enough.
 */
export function darkTooltip(trigger: 'axis' | 'item' = 'axis') {
  return {
    trigger,
    backgroundColor: TOOLTIP_BG,
    borderColor: 'transparent',
    textStyle: { color: '#fff', fontSize: 12 },
    enterable: true,
    hideDelay: 300,
  } as const;
}

/**
 * Smart tooltip position function — keeps the tooltip on screen.
 * Assign directly as `tooltip.position`.
 *
 * Usage:
 *   tooltip: { ...darkTooltip(), position: tooltipPosition }
 */
export function tooltipPosition(
  point: number[],
  _params: unknown,
  _dom: unknown,
  _rect: unknown,
  size: { contentSize?: number[]; viewSize?: number[] },
): [number, number] {
  const [x, y] = point;
  const tw = size?.contentSize?.[0] ?? 200;
  const vw = size?.viewSize?.[0] ?? 800;
  return x + tw + 20 > vw ? [x - tw - 20, y] : [x + 20, y];
}


/** Standard bottom legend with circular icons. Pass extra fields to override. */
export function bottomLegend(extra?: Record<string, unknown>) {
  return {
    bottom: 0,
    itemWidth: 10,
    itemHeight: 10,
    icon: 'circle',
    textStyle: { fontSize: 12, color: AXIS_COLOR },
    ...extra,
  };
}


export interface GridOpts {
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
}

/** Standard chart grid with `containLabel: true`. */
export function standardGrid({ left = 16, right = 16, top = 12, bottom = 32 }: GridOpts = {}) {
  return { left, right, top, bottom, containLabel: true };
}


/** Category x-axis (months, session types, hours…). Optionally pass a value `formatter`. */
export function categoryXAxis(data: string[], formatter?: (v: string) => string) {
  return {
    type: 'category' as const,
    data,
    axisLabel: {
      fontSize: 11,
      color: AXIS_COLOR,
      ...(formatter ? { formatter } : {}),
    },
    axisLine: { show: false },
    axisTick: { show: false },
  };
}

export interface ValueYAxisOpts {
  name?: string;
  min?: number;
  max?: number;
  minInterval?: number;
  formatter?: (v: number) => string;
}

/** Value y-axis with consistent colour, no axis line/tick, light grid. */
export function valueYAxis({ name, min, max, minInterval, formatter }: ValueYAxisOpts = {}) {
  return {
    type: 'value' as const,
    ...(name   !== undefined ? { name, nameTextStyle: { color: AXIS_COLOR, fontSize: 11 } } : {}),
    ...(min    !== undefined ? { min }    : {}),
    ...(max    !== undefined ? { max }    : {}),
    ...(minInterval !== undefined ? { minInterval } : {}),
    axisLine: { show: false },
    axisTick: { show: false },
    splitLine: { lineStyle: { color: GRID_LINE_COLOR } },
    axisLabel: {
      fontSize: 11,
      color: AXIS_COLOR,
      ...(formatter ? { formatter } : {}),
    },
  };
}


/**
 * Formats "yyyy-MM" to a compact label like "Jan '25".
 * Used as axis formatter and in tooltip headers.
 */
export function formatMonth(month: string): string {
  const [year, m] = month.split('-');
  return new Date(Number(year), Number(m) - 1, 1)
    .toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
}


/** Returns the sorted union of all months that appear in any player's data. */
export function collectAllAvailableMonths(
  playerMonths: { months: { month: string }[] }[],
): string[] {
  const set = new Set<string>();
  playerMonths.forEach((pm) => pm.months.forEach((m) => set.add(m.month)));
  return Array.from(set).sort();
}

/**
 * Returns exactly `n` months ending at the latest available month minus `offset`.
 * offset=0 → latest window; offset=1 → one month earlier; etc.
 * Use `collectAllAvailableMonths` to derive `maxOffset = allMonths.length - 1`.
 */
export function collectLastMonths(
  playerMonths: { months: { month: string }[] }[],
  n: number,
  offset = 0,
): string[] {
  const sorted = collectAllAvailableMonths(playerMonths);
  if (sorted.length === 0) return [];
  const latestIdx = sorted.length - 1 - offset;
  if (latestIdx < 0) return [];
  const latest = sorted[latestIdx];
  const [y, m] = latest.split('-').map(Number);
  const months: string[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const date = new Date(y, m - 1 - i, 1);
    months.push(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
  }
  return months;
}

/** `#rrggbb` → `rgba(r,g,b,alpha)`; any other colour format is returned untouched. */
export function withAlpha(color: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(color);
  if (!m) return color;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}
